import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Hash, Plus } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Button } from '@/components/ui/button';
import {
  useActiveAgents,
  useAssignNumber,
  useBuyNumber,
  usePhoneNumbers,
  useReleaseNumber,
  useRotateNumber,
} from '@/features/admin/numbers-queries';
import { getInitials } from '@/lib/format';

export const Route = createFileRoute('/_auth/admin/numbers')({ component: AdminNumbersPage });

type StatusKey = 'active' | 'cooling' | 'blacklisted' | 'unassigned';

function statusClass(status: string | null | undefined): string {
  const s = (status ?? '').toLowerCase();
  if (s === 'active') return 'bg-success/15 text-success border-success/30';
  if (s === 'cooling') return 'bg-warn/15 text-warn border-warn/30';
  if (s === 'blacklisted' || s === 'banned') return 'bg-danger/15 text-danger border-danger/30';
  return 'bg-surface-hi text-text-dim border-border/[0.15]';
}

function AdminNumbersPage(): JSX.Element {
  const { data: numbers, isLoading } = usePhoneNumbers();
  const { data: agents } = useActiveAgents();
  const assign = useAssignNumber();
  const release = useReleaseNumber();
  const rotate = useRotateNumber();
  const buy = useBuyNumber();
  const [pickedAgent, setPickedAgent] = useState<Record<string, string>>({});

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="flex items-center justify-between border-b border-border/[0.08] pl-14 pr-6 md:px-6 py-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
              Twilio fleet
            </div>
            <h1 className="mt-1 font-display text-2xl text-text">Numbers</h1>
            <p className="mt-1 text-sm text-text-dim">
              Phone number assignment, cooling, and lifecycle.
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => buy.mutate({})}
            disabled={buy.isPending}
            className="gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            {buy.isPending ? 'Buying…' : 'Buy New Number'}
          </Button>
        </div>

        {isLoading ? (
          <div className="p-6 text-text-mute">Loading…</div>
        ) : (
          <div className="px-6 py-5">
            <div className="overflow-hidden rounded-xl border border-border/[0.08] bg-surface-2">
              <div className="grid grid-cols-[1.2fr_120px_1.4fr_110px_110px_80px_220px] gap-3 border-b border-border/[0.08] px-4 py-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-text-mute">
                <div>Number</div>
                <div>Status</div>
                <div>Assigned To</div>
                <div>Assigned</div>
                <div>Cooling Until</div>
                <div className="text-right">Spam</div>
                <div className="text-right">Actions</div>
              </div>

              {(numbers ?? []).length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-text-mute">
                  <Hash className="h-5 w-5" />
                  <span className="text-sm">No numbers yet. Click "Buy New Number".</span>
                </div>
              ) : (
                numbers?.map((n) => (
                  <div
                    key={n.id}
                    className="grid grid-cols-[1.2fr_120px_1.4fr_110px_110px_80px_220px] items-center gap-3 border-b border-border/[0.08] px-4 py-3 text-sm transition-all duration-fast last:border-b-0 hover:-translate-y-px hover:border-border-hi hover:bg-surface-hi hover:shadow-[0_4px_16px_-8px_rgb(var(--accent)/0.25)]"
                  >
                    <div className="font-mono text-[13px] text-text">{n.phone_number}</div>
                    <div>
                      <span
                        className={`inline-flex rounded border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${statusClass(n.status)}`}
                      >
                        {n.status ?? '—'}
                      </span>
                    </div>
                    <div>
                      {n.profiles?.full_name ? (
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent">
                            {getInitials(n.profiles.full_name)}
                          </span>
                          <span className="truncate text-text">{n.profiles.full_name}</span>
                        </div>
                      ) : (
                        <select
                          className="w-full rounded border border-border/[0.15] bg-surface px-2 py-1 text-xs text-text outline-none focus:border-accent/50"
                          value={pickedAgent[n.id] ?? ''}
                          onChange={(e) =>
                            setPickedAgent((s) => ({ ...s, [n.id]: e.target.value }))
                          }
                        >
                          <option value="">— pick agent —</option>
                          {(agents ?? []).map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.full_name ?? a.id} ({a.role})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="font-mono text-[11.5px] text-text-dim">
                      {n.assigned_at ? new Date(n.assigned_at).toLocaleDateString('en-GB') : '—'}
                    </div>
                    <div className="font-mono text-[11.5px] text-text-dim">
                      {n.cooling_until ? new Date(n.cooling_until).toLocaleDateString('en-GB') : '—'}
                    </div>
                    <div className="text-right font-mono text-[12px] text-text">
                      {n.spam_reports ?? 0}
                    </div>
                    <div className="flex justify-end gap-1.5">
                      {!n.assigned_to && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!pickedAgent[n.id] || assign.isPending}
                          onClick={() =>
                            pickedAgent[n.id] &&
                            assign.mutate({ id: n.id, agentId: pickedAgent[n.id] as string })
                          }
                        >
                          Assign
                        </Button>
                      )}
                      {n.assigned_to && (
                        <Button size="sm" variant="outline" onClick={() => release.mutate({ id: n.id })}>
                          Release
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => rotate.mutate({ id: n.id })}>
                        Rotate
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// Suppress unused-type warnings (StatusKey kept for future status enum tightening)
export type { StatusKey };
